import {AfterViewInit, Component, Input} from '@angular/core';
import {TinyColor} from "@ctrl/tinycolor";
import {FormControl, FormGroup} from "@angular/forms";
import {applyTheme, argbFromHex, themeFromSourceColor} from "@material/material-color-utilities";
import {StylingColorSettings} from "../../../../core/models";
import {YesNoDialogService} from "../../../../core/shared-components/yes-no-dialog/yes-no-dialog.service";
import {COLOR_GROUPS, getColorExplanation, getColorLabel, Key} from "./color-selection-helper";

// Component metadata
@Component({
  selector: 'app-color-selection',
  templateUrl: './color-selection.component.html',
  styleUrls: ['./color-selection.component.scss'],
  standalone: false
})
// UI for selecting and previewing theme colors
export class ColorSelectionComponent implements AfterViewInit{
  // Input form model holding all color controls
  @Input() colorSelection: FormGroup;
  trackByKey = (_: number, key: Key) => key;

  constructor(private yesNoDialog: YesNoDialogService) {
  }

  // Initialize theme preview based on current form values
  ngAfterViewInit() {
    this.setTheme(this.colorSelection.value);
  }

  // Helper to read a form control value by name
  getColor(control: string): string {
    return this.colorSelection.get(control)?.value;
  }

  // Choose readable text color based on background lightness
  getTextColor(col: string): '#000000' | '#ffffff' {
    return new TinyColor(col).isLight() ? '#000000' : '#ffffff';
  }

  setColor(control: string, color: string): void { // The user selects the specific color
    setTimeout(() => {
      switch (control) {
        case 'primaryColor':
          this.primaryColor.setValue(color);
          break
        case 'onPrimary':
          this.onPrimary.setValue(color);
          break
        case 'secondaryColor':
          this.secondaryColor.setValue(color);
          break
        case 'onSecondary':
          this.onSecondary.setValue(color);
          break
        case 'primaryContainer':
          this.primaryContainer.setValue(color);
          break
        case 'onPrimaryContainer':
          this.onPrimaryContainer.setValue(color);
          break
        case 'secondaryContainer':
          this.secondaryContainer.setValue(color);
          break
        case 'onSecondaryContainer':
          this.onSecondaryContainer.setValue(color);
          break
        case 'warnColor':
          this.warnColor.setValue(color);
          break
        case 'backgroundColor':
          this.backgroundColor.setValue(color);
          break
        case 'matCardColor':
          this.matCardColor.setValue(color);
          break
        case 'headerTextColor':
          this.headerTextColor.setValue(color);
          break
        case 'textColor':
          this.textColor.setValue(color);
          break
        case 'flatButtonColor':
          this.flatButtonColor.setValue(color);
          break
        case 'onFlatButtonColor':
          this.onFlatButtonColor.setValue(color);
          break
        case 'outline':
          this.outline.setValue(color);
          break
        case 'onSecondaryButtonColor':
          this.onSecondaryButtonColor.setValue(color);
        case 'selectionColor':
          this.selectionColor.setValue(color);
          break
        case 'onSelectionColor':
          this.onSelectionColor.setValue(color);
          break
      }
    }, 100)
  }

  // Build a Material theme from primary/secondary/error and apply it to the preview
  generateThemeColors(): void { // Let angular generate the colors
    const theme = themeFromSourceColor(argbFromHex(this.primaryColor.value));
    this.yesNoDialog.open({
      title: 'Generer nyt tema',
      content: 'Vil du lade angular generere container farverne? Farvene kan vælges enkeltvis efterfølgende',
      yes: 'Ja',
      no: 'Nej'
    }).afterClosed().subscribe(confirm => {
      if (confirm) {
        applyTheme(theme, {target: document.getElementById('color-selection'), dark: false});


        // TOdo: check with morten what colors to overwrite. Do we keep some or overwrite all?

        const el = document.getElementById('customer-styling');
        // Keep primary and secondary color. Generate all others
        el.style.setProperty('--md-sys-color-primary', this.primaryColor.value);
        el.style.setProperty('--md-sys-color-on-primary', this.getTextColor(this.primaryColor.value));
        el.style.setProperty('--md-sys-color-secondary', this.secondaryColor.value);
        el.style.setProperty('--md-sys-color-on-secondary', this.getTextColor(this.secondaryColor.value));
        el.style.setProperty('--md-sys-color-background', this.backgroundColor.value);
        el.style.setProperty('--md-sys-color-error', this.warnColor.value);

        this.setTheme(this.generatedTheme);
      }
    })
  }

  // Apply theme values to the form and compute contrast text colors
  setTheme(theme: Record<keyof StylingColorSettings, string>) {
    Object.keys(theme).forEach((key: string) => {
      if (theme[key] != null) {
        this.setColor(key, theme[key]!);
      }
    });
  }

  // Read computed CSS variables from the preview container
  get generatedTheme(): Record<keyof StylingColorSettings, string> | null {
    const el = document.getElementById('customer-styling');
    if (!el) return null;

    const styledDiv = getComputedStyle(el);

    return {
      primaryColor: styledDiv.getPropertyValue('--md-sys-color-primary').trim(),
      onPrimary: styledDiv.getPropertyValue('--md-sys-color-on-primary').trim(),
      primaryContainer: styledDiv.getPropertyValue('--md-sys-color-primary-container').trim(),
      onPrimaryContainer: styledDiv.getPropertyValue('--md-sys-color-on-primary-container').trim(),

      secondaryColor: styledDiv.getPropertyValue('--md-sys-color-secondary').trim(),
      onSecondary: styledDiv.getPropertyValue('--md-sys-color-on-secondary').trim(),
      secondaryContainer: styledDiv.getPropertyValue('--md-sys-color-secondary-container').trim(),
      onSecondaryContainer: styledDiv.getPropertyValue('--md-sys-color-on-secondary-container').trim(),

      warnColor: styledDiv.getPropertyValue('--md-sys-color-error').trim(),

      backgroundColor: styledDiv.getPropertyValue('--md-sys-color-background').trim(),
      textColor: this.textColor.value,
      headerTextColor: this.headerTextColor.value,
      matCardColor: this.matCardColor.value,

      // customer button colors
      flatButtonColor: this.flatButtonColor.value,
      onFlatButtonColor: this.onFlatButtonColor.value,
      outline: styledDiv.getPropertyValue('--md-sys-color-outline').trim(),
      onSecondaryButtonColor: this.onSecondaryButtonColor.value,

      selectionColor: this.selectionColor.value,
      onSelectionColor: this.onSelectionColor.value,


      // --- Generated but not set by user ---
      // tertiary: styledDiv.getPropertyValue('--md-sys-color-tertiary').trim(),
      // onTertiary: styledDiv.getPropertyValue('--md-sys-color-on-tertiary').trim(),
      // tertiaryContainer: styledDiv.getPropertyValue('--md-sys-color-tertiary-container').trim(),
      // onTertiaryContainer: styledDiv.getPropertyValue('--md-sys-color-on-tertiary-container').trim(),
      // onError: styledDiv.getPropertyValue('--md-sys-color-on-error').trim(),
      // errorContainer: styledDiv.getPropertyValue('--md-sys-color-error-container').trim(),
      // onErrorContainer: styledDiv.getPropertyValue('--md-sys-color-on-error-container').trim(),
      // onBackground: styledDiv.getPropertyValue('--md-sys-color-on-background').trim(),
      // surface: styledDiv.getPropertyValue('--md-sys-color-surface').trim(),
      // onSurface: styledDiv.getPropertyValue('--md-sys-color-on-surface').trim(),
      // surfaceVariant: styledDiv.getPropertyValue('--md-sys-color-surface-variant').trim(),
      // onSurfaceVariant: styledDiv.getPropertyValue('--md-sys-color-on-surface-variant').trim(),

      // outlineVariant: styledDiv.getPropertyValue('--md-sys-color-outline-variant').trim(),

      // shadow: styledDiv.getPropertyValue('--md-sys-color-shadow').trim(),
      // scrim: styledDiv.getPropertyValue('--md-sys-color-scrim').trim(),

      // inverseSurface: styledDiv.getPropertyValue('--md-sys-color-inverse-surface').trim(),
      // inverseOnSurface: styledDiv.getPropertyValue('--md-sys-color-inverse-on-surface').trim(),
      // inversePrimary: styledDiv.getPropertyValue('--md-sys-color-inverse-primary').trim(),
    };
  }

  // Form control getters (bridge between template and form model)
  get primaryColor(): FormControl {
    return this.colorSelection.get('primaryColor') as FormControl;
  }
  private get onPrimary(): FormControl {
    return this.colorSelection.get('onPrimary') as FormControl;
  }
  private get secondaryColor(): FormControl {
    return this.colorSelection.get('secondaryColor') as FormControl;
  }
  private get onSecondary(): FormControl {
    return this.colorSelection.get('onSecondary') as FormControl;
  }
  private get primaryContainer(): FormControl {
    return this.colorSelection.get('primaryContainer') as FormControl;
  }
  private get onPrimaryContainer(): FormControl {
    return this.colorSelection.get('onPrimaryContainer') as FormControl;
  }
  private get secondaryContainer(): FormControl {
    return this.colorSelection.get('secondaryContainer') as FormControl;
  }
  private get onSecondaryContainer(): FormControl {
    return this.colorSelection.get('onSecondaryContainer') as FormControl;
  }
  private get flatButtonColor(): FormControl {
    return this.colorSelection.get('flatButtonColor') as FormControl;
  }
  private get onFlatButtonColor(): FormControl {
    return this.colorSelection.get('onFlatButtonColor') as FormControl;
  }
  private get outline(): FormControl {
    return this.colorSelection.get('outline') as FormControl;
  }
  private get onSecondaryButtonColor(): FormControl {
    return this.colorSelection.get('onSecondaryButtonColor') as FormControl;
  }
  private get selectionColor(): FormControl {
    return this.colorSelection.get('selectionColor') as FormControl;
  }
  private get onSelectionColor(): FormControl {
    return this.colorSelection.get('onSelectionColor') as FormControl;
  }

  get warnColor(): FormControl {
    return this.colorSelection.get('warnColor') as FormControl;
  }
  get matCardColor(): FormControl {
    return this.colorSelection.get('matCardColor') as FormControl;
  }
  get textColor(): FormControl {
    return this.colorSelection.get('textColor') as FormControl;
  }
  get headerTextColor(): FormControl {
    return this.colorSelection.get('headerTextColor') as FormControl;
  }
  get backgroundColor(): FormControl {
    return this.colorSelection.get('backgroundColor') as FormControl;
  }

  protected readonly getColorLabel = getColorLabel;
  protected readonly getColorExplanation = getColorExplanation;
  readonly colorGroups = COLOR_GROUPS;
}
